import React, { useState, useEffect } from 'react';
import {
  Copy,
  CheckCircle,
  XCircle,
  ExternalLink,
  Download,
  Loader2,
  AlertCircle,
  QrCode as QrCodeIcon,
  DollarSign,
  Calendar,
  User,
  FileText,
  Phone,
  Mail,
  Check,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ResultadoCobrancaPix() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [sucesso, setSucesso] = useState(null);
  const [copiado, setCopiado] = useState({ link: false, qr: false });
  const [gerandoPdf, setGerandoPdf] = useState(false);

  useEffect(() => {
    try {
      console.log('📥 Carregando dados do link PIX...');

      // ⭐ BUSCAR DADOS (igual ResultadoLinkAleatorio)
      const dadosStorage = localStorage.getItem('linkPixCobranca');
      
      if (!dadosStorage) {
        setErro('Nenhum dado encontrado. Esta janela deve ser aberta através da página de Cobrança PIX.');
        console.warn('⚠️ Nenhum dado no localStorage');
        setLoading(false);
        return;
      }

      const dadosParsed = JSON.parse(dadosStorage);
      
      // Verificar idade dos dados (10 minutos)
      const dataAtual = Date.now();
      const dataGerado = dadosParsed.timestamp || dataAtual;
      const diferencaMinutos = (dataAtual - dataGerado) / 1000 / 60;
      
      if (diferencaMinutos > 10) {
        console.warn(`⚠️ Dados com ${diferencaMinutos.toFixed(1)} minutos`);
      }
      
      setDados(dadosParsed);
      setLoading(false);
      console.log('✅ Dados carregados:', dadosParsed);
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setErro('Erro ao carregar dados do link PIX.');
      setLoading(false);
    }
  }, []);


  /**
   * Copiar para área de transferência
   */
  const copiarTexto = (texto, tipo) => {
    navigator.clipboard.writeText(texto).then(() => {
      setCopiado({ ...copiado, [tipo]: true });
      setSucesso(`${tipo === 'link' ? 'Link' : 'QR Code'} copiado com sucesso!`);
      
      setTimeout(() => {
        setCopiado({ ...copiado, [tipo]: false });
        setSucesso(null);
      }, 3000);
    }).catch((error) => {
      console.error('Erro ao copiar:', error);
      setErro('Erro ao copiar para área de transferência.');
    });
  };

  /**
   * ⭐ BAIXAR PDF COMPLETO
   */
  const baixarPDF = async () => {
    try {
      setGerandoPdf(true);
      console.log('📄 Gerando PDF...');
      
      // Importar jsPDF dinamicamente
      const { default: jsPDF } = await import('jspdf');
      
      const doc = new jsPDF();
      
      // Configurações
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPosition = 20;
      
      // ====== CABEÇALHO ======
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('PAGAMENTO PIX - COBRANÇA', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Dantas Eletro - Sistema de Cobrança', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      doc.setTextColor(0, 0, 0);
      
      // ====== INFORMAÇÕES DO CLIENTE ======
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('DADOS DO CLIENTE', margin, yPosition);
      yPosition += 8;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Nome: ${dados.cliente.nome}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Código: ${dados.cliente.codigo}`, margin, yPosition);
      yPosition += 6;
      doc.text(`CPF/CNPJ: ${dados.cliente.cpf_cnpj}`, margin, yPosition);
      yPosition += 12;
      
      // ====== INFORMAÇÕES DA PARCELA ======
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('DADOS DA PARCELA', margin, yPosition);
      yPosition += 8;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Nota Fiscal: ${dados.conta.nota}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Vencimento Original: ${format(new Date(dados.conta.data_vencimento), 'dd/MM/yyyy')}`, margin, yPosition);
      yPosition += 6;
      doc.text(`Valor Original: R$ ${dados.conta.valor_original.toFixed(2)}`, margin, yPosition);
      yPosition += 6;
      
      // Valor cobrado em destaque
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 150, 0);
      doc.setFontSize(12);
      doc.text(`Valor Cobrado: R$ ${dados.valor_cobrado.toFixed(2)}`, margin, yPosition);
      yPosition += 6;
      
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.text(`Dias em Atraso: ${dados.conta.dias_atraso}`, margin, yPosition);
      yPosition += 12;
      
      // ====== INFORMAÇÕES DE CONTATO ======
      if (dados.celular || dados.email) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('ENVIADO PARA', margin, yPosition);
        yPosition += 8;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        
        if (dados.celular) {
          const statusWpp = dados.status_envio?.whatsapp ? '✓ Enviado' : '✗ Não enviado';
          doc.text(`WhatsApp: ${dados.celular} ${statusWpp}`, margin, yPosition);
          yPosition += 6;
        }
        
        if (dados.email) {
          const statusEmail = dados.status_envio?.email ? '✓ Enviado' : '✗ Não enviado';
          doc.text(`Email: ${dados.email} ${statusEmail}`, margin, yPosition);
          yPosition += 6;
        }
        
        yPosition += 6;
      }
      
      // ====== INFORMAÇÕES DO PIX ======
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text('INFORMAÇÕES DO PIX', margin, yPosition);
      yPosition += 8;
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      
      const dataExpiracao = dados.pix?.expires_at 
        ? format(new Date(dados.pix.expires_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
        : 'Não especificada';
      
      doc.text(`Data de Expiração: ${dataExpiracao}`, margin, yPosition);
      yPosition += 6;
      doc.text(`ID do Pedido: ${dados.pedido_id || 'N/A'}`, margin, yPosition);
      yPosition += 12;
      
      // ====== QR CODE ======
      if (dados.pix?.qr_code_url || dados.link_id) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text('QR CODE PIX', margin, yPosition);
        yPosition += 8;
        
        try {
          const img = new Image();
          
          if (dados.link_id) {
            const localImageUrl = `${window.location.origin}/api/pix/qrcode/${dados.link_id}`;
            console.log('📱 Usando QR Code local:', localImageUrl);
            img.src = localImageUrl;
          } else if (dados.pix?.qr_code_url) {
            console.log('🌐 Usando QR Code externo:', dados.pix.qr_code_url);
            img.crossOrigin = 'Anonymous';
            img.src = dados.pix.qr_code_url;
          } else {
            throw new Error('Nenhuma fonte de QR Code disponível');
          }
          
          await new Promise((resolve, reject) => {
            img.onload = () => {
              console.log('✅ Imagem QR Code carregada');
              resolve();
            };
            img.onerror = (error) => {
              console.error('❌ Erro ao carregar QR Code:', error);
              reject(new Error('Erro ao carregar QR Code'));
            };
          });
          
          // Adicionar QR Code centralizado
          const qrSize = 80;
          const qrX = (pageWidth - qrSize) / 2;
          
          doc.addImage(img, 'PNG', qrX, yPosition, qrSize, qrSize);
          yPosition += qrSize + 10;
          
          // Texto informativo
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
          doc.setTextColor(100, 100, 100);
          doc.text('Escaneie o QR Code acima com o app do seu banco', pageWidth / 2, yPosition, { align: 'center' });
          doc.setTextColor(0, 0, 0);
          yPosition += 10;
          
        } catch (error) {
          console.error('❌ Erro ao adicionar QR Code ao PDF:', error);
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(9);
          doc.text('(QR Code não disponível para inclusão no PDF)', margin, yPosition);
          yPosition += 10;
        }
      }
      
      // ====== CÓDIGO PIX COPIA E COLA ======
      if (dados.pix?.qr_code) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('CÓDIGO PIX (Copia e Cola):', margin, yPosition);
        yPosition += 6;
        
        doc.setFont('courier', 'normal');
        doc.setFontSize(8);
        
        const qrCode = dados.pix.qr_code;
        const maxWidth = pageWidth - (margin * 2);
        const lines = doc.splitTextToSize(qrCode, maxWidth);
        
        lines.forEach(line => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, margin, yPosition);
          yPosition += 4;
        });
        
        yPosition += 6;
      }
      
      // ====== LINK DE PAGAMENTO ======
      if (dados.checkout_url) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text('LINK DE PAGAMENTO:', margin, yPosition);
        yPosition += 6;
        
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 255);
        
        const linkLines = doc.splitTextToSize(dados.checkout_url, pageWidth - (margin * 2));
        linkLines.forEach(line => {
          if (yPosition > 270) {
            doc.addPage();
            yPosition = 20;
          }
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });
        
        doc.setTextColor(0, 0, 0);
      }
      
      // ====== RODAPÉ ======
      doc.setFont('helvetica', 'italic');
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Documento gerado em: ${format(new Date(), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}`,
        pageWidth / 2,
        280,
        { align: 'center' }
      );
      
      // Salvar PDF
      const nomeArquivo = `PIX_Cobranca_${dados.cliente.codigo}_${dados.conta.nota}_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`;
      doc.save(nomeArquivo);
      
      console.log('✅ PDF gerado com sucesso:', nomeArquivo);
      setSucesso('PDF baixado com sucesso!');
      setTimeout(() => setSucesso(null), 3000);
      
    } catch (error) {
      console.error('❌ Erro ao gerar PDF:', error);
      setErro('Erro ao gerar PDF: ' + error.message);
      setTimeout(() => setErro(null), 5000);
    } finally {
      setGerandoPdf(false);
    }
  };

  /**
   * Formatar moeda
   */
  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Carregando dados do link PIX...</p>
        </div>
      </div>
    );
  }

  if (erro && !dados) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 text-center mb-2">
            Erro ao Carregar
          </h2>
          <p className="text-gray-600 text-center mb-6">{erro}</p>
          <button
            onClick={() => window.close()}
            className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Fechar Janela
          </button>
        </div>
      </div>
    );
  }

  if (!dados) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Cabeçalho */}
        <div className="bg-gradient-to-r from-green-600 to-green-800 rounded-lg shadow-lg p-6 text-white mb-6">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-white/10 rounded-lg">
              <CheckCircle className="w-8 h-8" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Link PIX Gerado com Sucesso!</h1>
              <p className="text-green-100 mt-1">
                O link de pagamento foi criado e enviado para o cliente
              </p>
            </div>
          </div>
        </div>

        {/* Mensagens de feedback */}
        {erro && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
            <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Erro</p>
              <p className="text-sm text-red-700 mt-1">{erro}</p>
            </div>
            <button 
              onClick={() => setErro(null)}
              className="text-red-400 hover:text-red-600"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {sucesso && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-green-800">Sucesso</p>
              <p className="text-sm text-green-700 mt-1">{sucesso}</p>
            </div>
            <button 
              onClick={() => setSucesso(null)}
              className="text-green-400 hover:text-green-600"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* ⭐ QR CODE PIX - LOGO APÓS O CABEÇALHO */}
        {dados.pix?.qr_code_url && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-8 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center flex items-center justify-center">
              <QrCodeIcon className="w-5 h-5 mr-2 text-gray-600" />
              QR CODE PIX
            </h2>
            <div className="flex flex-col items-center space-y-4">
              {/* ⭐ IMAGEM GRANDE E PROPORCIONAL */}
              <div className="bg-white p-6 rounded-xl border-2 border-gray-300 shadow-lg">
                <img
                  src={dados.pix.qr_code_url}
                  alt="QR Code PIX"
                  className="w-full max-w-md h-auto" 
                  style={{ maxWidth: '400px', width: '100%', height: 'auto' }}
                />
              </div>
              <p className="text-center text-sm text-gray-600 font-medium">
                📱 Escaneie com o app do seu banco
              </p>
            </div>
          </div>
        )}

        {/* Código PIX Copia e Cola */}
        {dados.pix?.qr_code && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <QrCodeIcon className="w-5 h-5 mr-2 text-gray-600" />
              Código PIX (Copia e Cola)
            </h2>
            <div className="flex space-x-2">
              <input
                type="text"
                value={dados.pix.qr_code}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
              />
              <button
                onClick={() => copiarTexto(dados.pix.qr_code, 'qr')}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                {copiado.qr ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copiar</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Status de Envio */}
        {(dados.celular || dados.email) && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Mail className="w-5 h-5 mr-2 text-gray-600" />
              Status de Envio
            </h2>
            <div className="space-y-3">
              {dados.celular && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">WhatsApp</p>
                      <p className="text-sm text-gray-600">{dados.celular}</p>
                    </div>
                  </div>
                  {dados.status_envio?.whatsapp ? (
                    <span className="flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      <Check className="w-4 h-4 mr-1" />
                      Enviado
                    </span>
                  ) : (
                    <span className="flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                      <X className="w-4 h-4 mr-1" />
                      Não enviado
                    </span>
                  )}
                </div>
              )}
              
              {dados.email && (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <p className="text-sm text-gray-600">{dados.email}</p>
                    </div>
                  </div>
                  {dados.status_envio?.email ? (
                    <span className="flex items-center px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      <Check className="w-4 h-4 mr-1" />
                      Enviado
                    </span>
                  ) : (
                    <span className="flex items-center px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                      <X className="w-4 h-4 mr-1" />
                      Não enviado
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Informações do Cliente e Conta */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Cliente */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="w-5 h-5 mr-2 text-gray-600" />
              Cliente
            </h2>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Nome</p>
                <p className="text-base font-medium text-gray-900">{dados.cliente.nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Código</p>
                <p className="text-base font-medium text-gray-900">{dados.cliente.codigo}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">CPF/CNPJ</p>
                <p className="text-base font-medium text-gray-900">{dados.cliente.cpf_cnpj}</p>
              </div>
            </div>
          </div>

          {/* Conta */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-gray-600" />
              Dados da Conta
            </h2>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Nota Fiscal</p>
                <p className="text-base font-medium text-gray-900">{dados.conta.nota}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Vencimento</p>
                <p className="text-base font-medium text-gray-900">
                  {format(new Date(dados.conta.data_vencimento), 'dd/MM/yyyy', { locale: ptBR })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Valor Original</p>
                <p className="text-base font-medium text-gray-900">
                  {formatarMoeda(dados.conta.valor_original)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Valor Cobrado</p>
                <p className="text-lg font-bold text-green-600">
                  {formatarMoeda(dados.valor_cobrado)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Link de Pagamento */}
        {dados.checkout_url && dados.checkout_url !== '#' && (
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ExternalLink className="w-5 h-5 mr-2 text-gray-600" />
              Link de Pagamento
            </h2>
            <div className="flex space-x-2">
              <input
                type="text"
                value={dados.checkout_url}
                readOnly
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
              />
              <button
                onClick={() => copiarTexto(dados.checkout_url, 'link')}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center space-x-2"
              >
                {copiado.link ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Copiado!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    <span>Copiar</span>
                  </>
                )}
              </button>
              
              <a
                href={dados.checkout_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center space-x-2"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Abrir</span>
              </a>
            </div>
          </div>
        )}

        {/* Informações do PIX */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-gray-600" />
            Informações do PIX
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">ID do Pedido</p>
              <p className="text-base font-medium text-gray-900">{dados.pedido_id || 'N/A'}</p>
            </div>
            {dados.pix?.expires_at && (
              <div>
                <p className="text-sm text-gray-600">Expira em</p>
                <p className="text-base font-medium text-gray-900">
                  {format(new Date(dados.pix.expires_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={baixarPDF}
            disabled={gerandoPdf}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {gerandoPdf ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Gerando PDF...</span>
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                <span>Baixar PDF</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => window.close()}
            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <XCircle className="w-5 h-5" />
            <span>Fechar Janela</span>
          </button>
        </div>
      </div>
    </div>
  );
}